import { Filter, FilterType, SetFilter } from '@helios/server-side-util'
import { Account } from '../models'

/**
 * Scopes an accountType Set filter to the account types a panel is allowed to
 * show.
 *
 * accountType is both the panel-partition key (which grid this is) and a
 * user-filterable set column on the sub-grid. Spreading the user's filter and
 * then hardcoding accountType clobbered their selection, which is why the
 * column appeared to do nothing (PVAL-26205). Intersecting instead lets the
 * user narrow within the panel's scope but never outside it.
 *
 * With no accountType filter at all this returns the full allowed set, i.e. the
 * original hardcoded behaviour. A filter that is present but selects nothing is
 * honoured as selecting nothing, not treated as absent.
 *
 * An empty result means the user asked only for types this panel never shows.
 * Callers MUST short-circuit to an empty result rather than sending it: the
 * backend ignores a set filter with no values, so the panel scoping would be
 * dropped along with it and every account type would leak into the grid.
 */
export const scopeAccountTypeFilter = (
  userFilter: Filter | undefined,
  allowedValues: Account['accountType'][],
): SetFilter => {
  const userValues = (userFilter as SetFilter | undefined)?.values

  return {
    filterType: FilterType.Set,
    values:
      userValues != null
        ? allowedValues.filter(value => userValues.includes(value))
        : allowedValues,
  }
}

/** @see scopeAccountTypeFilter for why an empty scope must not be sent. */
export const isEmptyAccountTypeScope = (filter?: Filter): boolean =>
  (filter as SetFilter | undefined)?.values?.length === 0
